import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useEventContext } from "./EventContext";
import { useLocaleContext } from "./LocaleContext";
import { useStreamContext } from "./StreamContext";
import { usePlayerContext } from "./PlayerContext";
import { useQuery } from "@offline/hooks/useQuery";
import tokenStorage from "../utils/tokenStorage";
import { TYPE_HYBRID } from "../constants/common";
import { USER_TYPE_ANONYMOUS } from "../constants/user";
import { loginGuest, logout } from "../utils/api";
import { loginExternal, loginWithAccessToken } from "@player-api";
import CModal from "../components/UI/CModal";
import LoadingMessage from "../components/LoadingMessage";
import UserAuthenticationModal from "../components/User/UserAuthenticationModal";
import Authentication from "../pages/Authentication";
import AccessRestricted from "../pages/Authentication/AccessRestricted";

const getUserFromToken = (token) => {
    let user = null;
    let userCometChannel = null;

    if (token) {
        const payload = jwtDecode(token);
        user = (payload?.clientRegistrationId ?? payload?.id)
            ? payload
            : (payload?.client || null);
        userCometChannel = payload?.userCometChannel;
    }

    return { user, userCometChannel };
}

const getParticipationTypeFromToken = (token, defaultParticipationType) => {
    let _participationType = defaultParticipationType;
    if (token) {
        const payload = jwtDecode(token);
        if (payload?.participationType) {
            _participationType = payload?.participationType;
        }
    }
    return participationTypeFilter(_participationType);
}

const getExternalAuthInfo = (externalAuth, registrationFields = []) => {
    let info = {};
    if (externalAuth?.name) {
        info.name = decodeURIComponent(externalAuth.name);
    }
    if (externalAuth?.registrationForm) {
        try {
            let data = JSON.parse(decodeURIComponent(externalAuth.registrationForm));
            info = {
                ...data,
                ...info,
                externalLogin: true,
            };
            registrationFields?.filter(field => field.field.type === 'disclaimer' && field.required)
                .forEach(field => info[field.field.name] = true)
        } catch (e) { }
    }
    return info;
}

export const checkUserInfo = (user, userParticipationType, event) => {

    if (event.moduleAuth.anonymous && user?.type === USER_TYPE_ANONYMOUS) return user;

    if (!user || (event.participationType === TYPE_HYBRID && !userParticipationType)) return null;

    const fields = (event.registrationFields || []).filter(field => field.required).map(field => field.field.name);
    return ['name', ...fields].every(field => user.info[field]) ? user : null;
}

export const participationTypeFilter = (type, defaultValue = '') => {
    type = type.toLowerCase();

    if (-1 !== ['', 'online', 'offline', 'hybrid'].indexOf(type)) {
        return type;
    }

    return defaultValue;
}

const modalAppendTo = document.body;

export const UserContext = createContext(null);

export const useUserContext = () => useContext(UserContext);

export const UserContextProvider = ({ preview, externalAuth, children, hasAuth = true }) => {
    const { event, watchKey, initData } = useEventContext();
    const $locale = useLocaleContext();
    const { clearPlayer } = usePlayerContext();
    const { setParticipationType: setStreamContextParticipationType } = useStreamContext();
    const query = useQuery();
    const navigate = useNavigate();
    const defaultParticipationType = localStorage.getItem(`participationType_${event.eventKey}`) || (event.participationType !== 'hybrid' ? event.participationType : '');
    const accessToken = query.get('accessToken');
    const [user, setUser] = useState(null);
    const [accessLinkEnabled, setAccessLinkEnabled] = useState(event.private ? !!(event.private && accessToken) : true);
    const [userCometChannel, setUserCometChannel] = useState(initData?.userCometChannel || null);
    const [participationType, setParticipationType] = useState(participationTypeFilter(defaultParticipationType));
    const [loginWithToken, setLoginWithToken] = useState(false);
    const [isAuthPageVisible, setAuthPageVisible] = useState(!preview && (event.moduleAuth.require ? !user : !participationType));
    const [tokenBlocked, setTokenBlocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [authModal, setAuthModal] = useState(false);
    const [userRole, setUserRole] = useState(initData?.role || null);

    const onCloseAuthModal = () => setAuthModal(false);

    const handleExternalAuth = useCallback((externalAuth, user) => {
        const externalAuthInfo = getExternalAuthInfo(externalAuth, event.registrationFields);
        const willLoginWithToken = event.moduleAuth.allowLoginWithToken && accessToken;
        const willLoginGuest = !user && event.moduleAuth.guest && externalAuthInfo?.name;
        const willLoginEmail = !user && event.moduleAuth.email && !!externalAuthInfo?.checkSum;

        if (willLoginWithToken || willLoginGuest || willLoginEmail) {
            let loginFunc = loginGuest;
            let payload = externalAuthInfo;
            if (willLoginWithToken) {
                loginFunc = loginWithAccessToken;
                payload = accessToken;
            } else if (willLoginEmail) {
                loginFunc = loginExternal;
            }
            setLoginWithToken(true);
            loginFunc(event.eventKey, payload)
                .catch((e) => console.error(e))
                .finally(() => {
                    setLoginWithToken(false);
                    setLoading(false);
                    navigate(`/event/${watchKey}`);
                });
        }
    }, [event, accessToken]);

    useEffect(() => {
        if (!event.moduleAuth.require && event.participationType !== 'hybrid') {
            setAuthPageVisible(false);
            localStorage.setItem(`participationType_${event.eventKey}`, event.participationType);
        }
    }, []);

    useEffect(() => {
        if (!event) {
            return;
        }

        tokenStorage.setEventKey(event.eventKey);

        let _user;

        const listener = () => {
            const token = tokenStorage.get();
            const userFromToken = getUserFromToken(token);
            _user = userFromToken.user;
            const userParticipationType = getParticipationTypeFromToken(token, defaultParticipationType);
            setParticipationType(userParticipationType);
            setUserCometChannel(userFromToken.userCometChannel);
            const checkUser = checkUserInfo(_user, userParticipationType, event);
            setUser(checkUser);
            setAuthModal(_user && !checkUser && hasAuth && !event.moduleAuth.require);
            setLoading(false);
        }

        tokenStorage.addEventListener(listener);
        listener();
        handleExternalAuth(externalAuth, _user);

        return () => {
            tokenStorage.removeEventListener(listener);
        }
    }, [event, externalAuth]);

    useEffect(() => {
        setStreamContextParticipationType(participationType);
    }, [participationType]);

    const handleLogout = async () => {
        try {
            await logout(event.eventKey);
        } catch (err) {
            tokenStorage.set('~');
            console.error(err);
        } finally {
            clearPlayer();
            navigate(`/event/${watchKey}`);
            setUserCometChannel(null);
            setAccessLinkEnabled(false);
        }
    }

    const handleChoiceParticipationType = (participationType) => {
        localStorage.setItem(`participationType_${event.eventKey}`, participationType);
        setParticipationType(participationType);
        setAuthPageVisible(false);
    }

    const isRoleUser = (roles = []) => roles.includes(userRole);

    const requireAuth = isAuthPageVisible && !user && hasAuth;

    if (loading) return null;

    if (loginWithToken) {
        return <LoadingMessage isPreloader>Авторизация</LoadingMessage>
    }

    if (event.private || (!!event.moduleAuth.maxActiveClientDevices && event.moduleAuth.email)) {
        const accessBlocked = !accessLinkEnabled && !user;

        if (tokenBlocked || accessBlocked) {
            return <AccessRestricted tokenBlocked={tokenBlocked} accessBlocked={accessBlocked} />
        }
    }

    if (requireAuth) {
        return (
            <Authentication
                onChangeVisibility={setAuthPageVisible}
                onChoiceParticipationType={handleChoiceParticipationType}
            />
        );
    }

    const context = {
        user,
        userCometChannel,
        setUserCometChannel,
        setTokenBlocked,
        participationType,
        setParticipationType, // For debug switch
        logout: handleLogout,
        hasAuthentication: hasAuth,
        requireAuth,
        userRole,
        setUserRole,
        isRoleUser,
    };

    return (
        <UserContext.Provider value={context}>
            <CModal
                isOpen={authModal}
                lockBody={false}
                hasBack={false}
                onClose={onCloseAuthModal}
                appendTo={modalAppendTo}
            >
                <UserAuthenticationModal event={event} $locale={$locale} onClose={onCloseAuthModal} />
            </CModal>
            {children}
        </UserContext.Provider>
    );
}
